/**
 * German copy for the refusals the **employment history** actions can answer with — re-hiring a former
 * employee, and editing one of the stints on their record (#140).
 *
 * Both sites used to render the server's `ApiError.description`. The comment above one of them even said the
 * quiet part out loud — *"overlap / end-before-start read as a clear message instead of a generic one"* — which
 * was true of the *information* and false of the *language*: that field comes from `ErrorCatalog` and is
 * developer-facing English by design, kept for Postman, logs and docs. See the frontend style guide,
 * *"Surfacing an API error — branch on `code`, never render `description`"*.
 *
 * Colocated with the screen rather than put in `_shared/errors/` because this vocabulary has exactly one
 * consumer; the shared module is for the guards that several surfaces enforce.
 *
 * **When a new code appears here**, add it with its own sentence read from that code's XML doc in
 * `API.Core/Authorization/EmploymentErrorCodes.cs`. Do not widen {@link describeEmploymentPeriodError} back to
 * `description`, and do not make the caller fallbacks more specific.
 */

import {readErrorCode} from '../../../_auth/two-factor/second-factor-errors';

/**
 * `ApiError.code` → German sentence.
 *
 * These three are the complete set the two endpoints can produce. `EmploymentErrorCodes` holds two more —
 * `E_EMPLOYMENT_ENDED` and `E_EMPLOYMENT_ALREADY_ENDED` — which belong to the access gates (login, account
 * switch, PIN step-up) and to ending an employment, and are unreachable from here; the entry gate's own
 * sentence for the first already lives in `_auth/context/context-entry-refusal.ts`.
 *
 * The other two failures of these endpoints — an unknown employee, and an empty period id — come back as
 * **bare** 404/400 with no `ApiError` body at all, and land on the caller's fallback.
 */
const EMPLOYMENT_PERIOD_MESSAGES: Readonly<Record<string, string>> = {
  /**
   * Answered by BOTH actions, and the wording has to fit both: on an edit it means the typed dates collide
   * with another stint, on a re-hire it almost always means the employment never actually ended. Naming the
   * re-hire case explicitly is the difference between "try other dates" and "check whether they ever left".
   */
  E_EMPLOYMENT_PERIOD_OVERLAP:
    'Dieser Zeitraum überschneidet sich mit einem bestehenden Beschäftigungszeitraum. Prüfen Sie die bereits ' +
    'erfassten Zeiträume — bei einer Wiedereinstellung besteht meist noch ein laufendes Arbeitsverhältnis.',
  /** The end must lie strictly after the start. A plain rule, so a plain sentence. */
  E_EMPLOYMENT_END_BEFORE_START:
    'Das Ende eines Beschäftigungszeitraums muss nach seinem Beginn liegen.',
  /** The row was gone by the time the edit arrived — a stale form, so the action is to reload. */
  E_EMPLOYMENT_PERIOD_NOT_FOUND:
    'Dieser Beschäftigungszeitraum wurde nicht gefunden — vermutlich wurde er zwischenzeitlich gelöscht. ' +
    'Bitte laden Sie die Seite neu.'
};

/**
 * The German sentence for a failed employment-history action.
 *
 * @param err      whatever rxjs handed the `error` callback.
 * @param fallback the German sentence for a failure this table does not recognise. Per call site, because
 *                 re-hiring names the person ("Ada Lovelace konnte nicht wieder eingestellt werden.") and
 *                 saving a stint names the stint. It must stay a fixed German sentence: an unmapped code
 *                 degrades to it, never to `description`.
 */
export function describeEmploymentPeriodError(err: unknown, fallback: string): string {
  const code = readErrorCode(err);
  return (code && EMPLOYMENT_PERIOD_MESSAGES[code]) || fallback;
}
